import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Rocket, Users, ClipboardList, UserPlus, ArrowRight, ArrowLeft,
  CheckCircle2, Loader2, PartyPopper, Hammer, Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";

const STEPS = [
  {
    key: "welcome",
    title: "Bienvenue sur Blitzz",
    subtitle: "Configurons votre espace en quelques minutes",
    icon: Rocket,
  },
  {
    key: "builder",
    title: "Votre Builder",
    subtitle: "Creez votre premier questionnaire pour collecter les infos de vos prospects",
    icon: Hammer,
  },
  {
    key: "team",
    title: "Votre equipe",
    subtitle: "Invitez vos collaborateurs pour travailler ensemble",
    icon: UserPlus,
  },
  {
    key: "import",
    title: "Importez vos donnees",
    subtitle: "Importez votre base clients et vos factures existantes",
    icon: Users,
  },
];

export default function OnboardingWizard({ onComplete }) {
  const { axiosAuth, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [questionnaireName, setQuestionnaireName] = useState("Formulaire de contact");
  const [inviteEmail, setInviteEmail] = useState("");
  const [invited, setInvited] = useState([]);
  const [questionnaireCreated, setQuestionnaireCreated] = useState(false);

  const api = axiosAuth();

  const handleCreateQuestionnaire = async () => {
    if (!questionnaireName.trim()) return;
    setLoading(true);
    try {
      await api.post("/builder/questionnaires", {
        name: questionnaireName,
        description: "Questionnaire cree lors de l'onboarding",
        fields: [
          { field_id: "f1", label: "Nom complet", type: "text", required: true },
          { field_id: "f2", label: "Email", type: "email", required: true },
          { field_id: "f3", label: "Telephone", type: "tel", required: false },
          { field_id: "f4", label: "Societe", type: "text", required: false },
          { field_id: "f5", label: "Message", type: "textarea", required: false },
        ],
      });
      setQuestionnaireCreated(true);
    } catch {}
    setLoading(false);
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setLoading(true);
    try {
      await api.post("/tenant/team/invite", { email: inviteEmail, role: "tenant_member" });
      setInvited([...invited, inviteEmail]);
      setInviteEmail("");
    } catch {}
    setLoading(false);
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await api.post("/onboarding/complete");
    } catch {}
    setLoading(false);
    if (onComplete) onComplete();
  };

  const canNext = () => {
    if (step === 0) return true;
    if (step === 1) return true; // builder step is optional
    if (step === 2) return true; // team invite optional
    if (step === 3) return true;
    return true;
  };

  const next = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else handleComplete();
  };

  const prev = () => {
    if (step > 0) setStep(step - 1);
  };

  const currentStep = STEPS[step];
  const Icon = currentStep.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center" style={{ background: "rgba(11, 8, 17, 0.95)", backdropFilter: "blur(20px)" }} data-testid="onboarding-wizard">
      <div className="w-full max-w-2xl mx-4">
        {/* Progress bar */}
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.1)" }}>
              <div className="h-full rounded-full transition-all duration-500" style={{ width: i <= step ? "100%" : "0%", background: "#f7c935" }} />
            </div>
          ))}
        </div>

        {/* Card */}
        <div className="rounded-2xl border p-8 md:p-12" style={{ background: "#14101c", borderColor: "rgba(255,255,255,0.1)" }}>
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: "rgba(247, 201, 53, 0.15)" }}>
              <Icon className="w-7 h-7" style={{ color: "#f7c935" }} />
            </div>
            <div>
              <h2 className="text-2xl font-black text-white">{currentStep.title}</h2>
              <p className="text-sm text-slate-400 mt-0.5">{currentStep.subtitle}</p>
            </div>
          </div>

          {/* Step content */}
          <div className="min-h-[200px]">
            {step === 0 && (
              <div className="space-y-6">
                <p className="text-slate-300 leading-relaxed">
                  Votre espace <strong style={{ color: "#f7c935" }}>{user?.tenant?.name || "Blitzz"}</strong> est pret.
                  En quelques etapes, vous allez :
                </p>
                <div className="space-y-3">
                  {[
                    { icon: Hammer, text: "Creer votre premier questionnaire de prospection" },
                    { icon: UserPlus, text: "Inviter les membres de votre equipe" },
                    { icon: Users, text: "Importer votre base clients et factures existantes" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: "rgba(255,255,255,0.03)" }}>
                      <item.icon className="w-5 h-5 flex-shrink-0" style={{ color: "#f7c935" }} />
                      <span className="text-sm text-slate-300">{item.text}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500">Vous pourrez revenir sur ces etapes plus tard dans les Parametres.</p>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5">
                <p className="text-sm text-slate-400">
                  Un questionnaire vous permet de collecter les informations de vos prospects directement depuis un lien partageable.
                </p>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Nom du questionnaire</label>
                  <input
                    type="text" value={questionnaireName} onChange={e => setQuestionnaireName(e.target.value)}
                    className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#f7c935]"
                    placeholder="Ex: Formulaire de contact, Demande de devis..."
                    data-testid="onboarding-questionnaire-name"
                  />
                </div>
                {questionnaireCreated ? (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                    <span className="text-sm text-green-400 font-medium">Questionnaire cree ! Vous pourrez le personnaliser dans le Builder.</span>
                  </div>
                ) : (
                  <Button onClick={handleCreateQuestionnaire} disabled={loading || !questionnaireName.trim()}
                    className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="onboarding-create-questionnaire-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Creer le questionnaire
                  </Button>
                )}
                <p className="text-[10px] text-slate-600">Vous pouvez passer cette etape et creer vos questionnaires plus tard.</p>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <p className="text-sm text-slate-400">
                  Ajoutez les membres de votre equipe pour collaborer sur la plateforme.
                </p>
                <div className="flex gap-2">
                  <input
                    type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                    className="flex-1 bg-[#0b0811] border border-white/10 text-white rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#f7c935]"
                    placeholder="email@collaborateur.fr"
                    data-testid="onboarding-invite-email"
                    onKeyDown={e => e.key === "Enter" && handleInvite()}
                  />
                  <Button onClick={handleInvite} disabled={loading || !inviteEmail.trim()}
                    className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold px-6" data-testid="onboarding-invite-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Inviter"}
                  </Button>
                </div>
                {invited.length > 0 && (
                  <div className="space-y-1.5">
                    {invited.map((e, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-green-500/10 border border-green-500/20 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                        <span className="text-green-400">{e}</span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-slate-600">Vous pouvez aussi inviter des membres depuis Mon equipe.</p>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <p className="text-sm text-slate-400">
                  Importez vos donnees existantes en quelques clics. L'IA se charge du classement et de l'integration.
                </p>
                <div className="grid gap-3">
                  {[
                    { label: "Importer mes clients", desc: "CSV, XLSX ou PDF — L'IA mappe automatiquement les colonnes", path: "/admin/import?tab=clients", icon: Users },
                    { label: "Importer mes echanges CRM", desc: "Appels, emails, notes — classes dans les fiches clients", path: "/admin/import?tab=crm", icon: ClipboardList },
                    { label: "Importer mes factures", desc: "Detection statut paye/impaye, doublons, classement client", path: "/admin/import?tab=invoices", icon: Sparkles },
                  ].map(item => (
                    <button key={item.path} onClick={() => { navigate(item.path); if (onComplete) onComplete(); }}
                      className="flex items-center gap-4 p-4 rounded-xl border text-left transition-all hover:border-[#f7c935]/40 group"
                      style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.06)" }}
                      data-testid={`onboarding-import-${item.label.replace(/[^a-z]/gi, "").toLowerCase()}`}>
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(247,201,53,0.1)" }}>
                        <item.icon className="w-5 h-5" style={{ color: "#f7c935" }} />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-bold text-white group-hover:text-[#f7c935] transition-colors">{item.label}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{item.desc}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-[#f7c935] transition-colors" />
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-slate-600">Vous pouvez importer vos donnees a tout moment depuis le menu Import.</p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/5">
            <button onClick={prev} disabled={step === 0}
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-white transition-colors disabled:opacity-0"
              data-testid="onboarding-prev-btn">
              <ArrowLeft className="w-4 h-4" /> Precedent
            </button>
            <div className="flex items-center gap-3">
              {!isLast && (
                <button onClick={handleComplete}
                  className="text-xs text-slate-600 hover:text-slate-400 transition-colors"
                  data-testid="onboarding-skip-btn">
                  Passer l'onboarding
                </button>
              )}
              <Button onClick={next} disabled={!canNext()} data-testid="onboarding-next-btn"
                className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold px-8">
                {isLast ? (
                  <><PartyPopper className="w-4 h-4 mr-2" /> Commencer</>
                ) : (
                  <>Suivant <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
